import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getUsers, createUser, updateUser, deleteUser, getRoles } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Trash2, Shield, ShieldCheck, User, Search } from "lucide-react";

const ROLES = [
  { id: "admin", name: "Administrator", icon: ShieldCheck, color: "text-red-600 bg-red-50" },
  { id: "manager", name: "Manager", icon: Shield, color: "text-blue-600 bg-blue-50" },
  { id: "user", name: "Benutzer", icon: User, color: "text-slate-600 bg-slate-50" }
];

export default function Users() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "user"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      role: "user"
    });
    setEditingUser(null);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: "",
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await updateUser(editingUser.id, updateData);
        toast.success("Benutzer aktualisiert");
      } else {
        await createUser(formData);
        toast.success("Benutzer erstellt");
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Möchten Sie den Benutzer "${email}" wirklich löschen?`)) return;
    try {
      await deleteUser(id);
      toast.success("Benutzer gelöscht");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? "Benutzer deaktiviert" : "Benutzer aktiviert");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const getRoleBadge = (roleId) => {
    const role = ROLES.find(r => r.id === roleId) || ROLES[2];
    const Icon = role.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${role.color}`}>
        <Icon className="w-3 h-3" />
        {role.name}
      </span>
    );
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div data-testid="users-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Benutzerverwaltung</h1>
          <p className="page-subtitle">Verwalten Sie Benutzer und Berechtigungen</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} className="btn-primary gap-2" data-testid="add-user-btn">
              <Plus className="w-4 h-4" />
              Benutzer hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingUser ? "Benutzer bearbeiten" : "Neuer Benutzer"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Vorname</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Max"
                    required
                    data-testid="user-firstname-input"
                  />
                </div>
                <div>
                  <Label className="form-label">Nachname</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Mustermann"
                    required
                    data-testid="user-lastname-input"
                  />
                </div>
              </div>
              <div>
                <Label className="form-label">E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="max@firma.de"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label className="form-label">
                  Passwort {editingUser && "(leer lassen um nicht zu ändern)"}
                </Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label className="form-label">Rolle</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          <role.icon className="w-4 h-4" />
                          {role.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="btn-primary" data-testid="user-submit-btn">
                  {editingUser ? "Speichern" : "Erstellen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {ROLES.map((role) => (
          <Card key={role.id} className="border-slate-200">
            <CardContent className="p-4 flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.color}`}>
                <role.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">{role.name}</p>
                <p className="text-sm text-slate-500">
                  {role.id === "admin" && "Voller Zugriff auf alle Funktionen inkl. Benutzerverwaltung"}
                  {role.id === "manager" && "Equipment, Mitarbeiter, Ausleihen, Verkäufe, Reports"}
                  {role.id === "user" && "Nur Lesen und Ausleihen erstellen"}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Suchen nach Name oder E-Mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="user-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">E-Mail</TableHead>
              <TableHead className="font-semibold">Rolle</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  Keine Benutzer gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell className="text-slate-600">{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    {user.is_active ? (
                      <span className="badge-success">Aktiv</span>
                    ) : (
                      <span className="badge-neutral">Inaktiv</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(user)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                          {user.is_active ? "Deaktivieren" : "Aktivieren"}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(user.id, user.email)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
